import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiBook, FiCheck, FiX, FiUser, FiCalendar, 
  FiMail, FiImage, FiTrash2, FiEye, FiEyeOff
} from 'react-icons/fi';
import { api } from '../../api';
import toast from 'react-hot-toast';

const GuestBookEntryAdmin = ({ entry, onApprove, onDelete }) => {
  const [imageError, setImageError] = useState(false);
  const [showFullMessage, setShowFullMessage] = useState(false);
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`bg-white rounded-xl shadow-md p-6 mb-4 ${
        !entry.is_approved ? 'border-2 border-yellow-400' : ''
      }`}
    >
      <div className="flex items-start space-x-4">
        {entry.photo_path && !imageError ? (
          <img
            src={entry.photo_path}
            alt={entry.name}
            onError={() => setImageError(true)}
            className="w-16 h-16 rounded-full object-cover cursor-pointer"
            onClick={() => window.open(entry.photo_path, '_blank')}
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-lodge-brown-100 flex items-center justify-center">
            <FiUser className="w-8 h-8 text-lodge-brown-600" />
          </div>
        )}
        
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-mountain-gray-800">{entry.name}</h3>
              <div className="flex items-center space-x-4 text-sm text-mountain-gray-500">
                {entry.email && (
                  <div className="flex items-center">
                    <FiMail className="w-4 h-4 mr-1" />
                    <a href={`mailto:${entry.email}`} className="hover:text-lodge-brown-600">
                      {entry.email}
                    </a>
                  </div>
                )}
                <div className="flex items-center">
                  <FiCalendar className="w-4 h-4 mr-1" />
                  {new Date(entry.created_at).toLocaleDateString()}
                </div>
              </div>
              {(entry.check_in && entry.check_out) ? (
                <p className="text-sm text-mountain-gray-600 mt-1">
                  Stayed: {new Date(entry.check_in).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {new Date(entry.check_out).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              ) : entry.stay_dates && (
                <p className="text-sm text-mountain-gray-600 mt-1">
                  Stayed: {entry.stay_dates}
                </p>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              {!entry.is_approved && (
                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                  Pending Approval
                </span>
              )}
            </div>
          </div>
          
          <div className="mt-3">
            <div className={`text-mountain-gray-700 whitespace-pre-wrap ${
              !showFullMessage && entry.message.length > 200 ? 'line-clamp-3' : ''
            }`}>
              {entry.message}
            </div>
            {entry.message.length > 200 && (
              <button
                onClick={() => setShowFullMessage(!showFullMessage)}
                className="text-sm text-lodge-brown-600 hover:text-lodge-brown-700 mt-1"
              >
                {showFullMessage ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
          
          <div className="flex items-center space-x-2 mt-4">
            {!entry.is_approved && (
              <button
                onClick={() => onApprove(entry.id)}
                className="flex items-center px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
              >
                <FiCheck className="w-4 h-4 mr-1" />
                Approve
              </button>
            )}
            
            <button
              onClick={() => onDelete(entry.id)}
              className="flex items-center px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
            >
              <FiTrash2 className="w-4 h-4 mr-1" />
              Delete
            </button>
            
            {entry.is_approved && (
              <button
                onClick={() => onApprove(entry.id, false)}
                className="flex items-center px-3 py-1 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
              >
                <FiEyeOff className="w-4 h-4 mr-1" />
                Hide
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const AdminGuestBook = () => {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('all'); // all, approved, pending
  
  const { data: entriesResponse, isLoading } = useQuery(
    'adminGuestbook',
    () => api.getAdminGuestbookEntries()
  );
  
  const entries = entriesResponse?.data || [];

  const approveMutation = useMutation(
    ({ id, approve = true }) => api.approveGuestbookEntry(id, approve),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminGuestbook');
        toast.success('Entry updated successfully');
      },
      onError: () => toast.error('Failed to update entry')
    }
  );

  const deleteMutation = useMutation(
    (id) => api.deleteGuestbookEntry(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminGuestbook');
        toast.success('Entry deleted successfully');
      },
      onError: () => toast.error('Failed to delete entry')
    }
  );

  const handleApprove = (id, approve = true) => {
    approveMutation.mutate({ id, approve });
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      deleteMutation.mutate(id);
    }
  };

  // Filter entries
  const filteredEntries = entries.filter(entry => {
    if (filter === 'approved') return entry.is_approved;
    if (filter === 'pending') return !entry.is_approved;
    return true;
  });

  // Stats
  const stats = {
    total: entries.length,
    approved: entries.filter(e => e.is_approved).length,
    pending: entries.filter(e => !e.is_approved).length,
    withPhotos: entries.filter(e => e.photo_path).length
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-display text-lodge-brown-800">Guest Book Management</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-mountain-gray-600">Total Entries</p>
              <p className="text-2xl font-bold text-mountain-gray-900">{stats.total}</p>
            </div>
            <FiBook className="w-8 h-8 text-lodge-brown-600" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-mountain-gray-600">Approved</p>
              <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
            </div>
            <FiEye className="w-8 h-8 text-green-600" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-mountain-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
            <FiEyeOff className="w-8 h-8 text-yellow-600" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-mountain-gray-600">With Photos</p>
              <p className="text-2xl font-bold text-sky-blue-600">{stats.withPhotos}</p>
            </div>
            <FiImage className="w-8 h-8 text-sky-blue-600" />
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex space-x-4 mb-6">
        {[
          { value: 'all', label: 'All Entries' },
          { value: 'pending', label: 'Pending Approval' },
          { value: 'approved', label: 'Approved' }
        ].map(tab => (
          <button
            key={tab.value}
            onClick={() => setFilter(tab.value)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === tab.value
                ? 'bg-lodge-brown-600 text-white'
                : 'bg-white text-mountain-gray-700 hover:bg-gray-50'
            }`}
          >
            {tab.label}
            {tab.value === 'pending' && stats.pending > 0 && (
              <span className="ml-2 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs">
                {stats.pending}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Entries List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lodge-brown-600"></div>
        </div>
      ) : filteredEntries.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <FiBook className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">
            {filter === 'pending' 
              ? 'No pending entries to review'
              : filter === 'approved'
              ? 'No approved entries yet'
              : 'No guest book entries yet'}
          </p>
        </div>
      ) : (
        <AnimatePresence>
          {filteredEntries.map(entry => (
            <GuestBookEntryAdmin
              key={entry.id}
              entry={entry}
              onApprove={handleApprove}
              onDelete={handleDelete}
            />
          ))}
        </AnimatePresence>
      )}
    </div>
  );
};

export default AdminGuestBook;